import { Globe, X } from 'lucide-react';
import { useUserCountryConfig } from '@/lib/countryConfig';

/**
 * "You're browsing another market" — shown only when the market on screen is
 * not the one this viewer belongs to.
 *
 * Switching market is a real feature (#438: a helper working across a border
 * needs it). What was missing is that the switch was *invisible and
 * one-way*: a helper's whole app — site copy, currency, the booking
 * flow's city list — switched to another market after one tap on the unlabelled
 * selector in Find Jobs, with nothing on screen saying so and no way back
 * except finding that same control again (owner report, 2026-08-26).
 *
 * The `source` field this reads has existed since #438 precisely so the UI
 * could disclose an override, and had no consumers until now.
 *
 * Deliberately a banner rather than a toast: the condition persists across
 * navigation, so the disclosure has to persist with it. A toast would say it
 * once and let the app go on lying afterwards.
 */
export function MarketNotice() {
  const { config, homeCountry, source, setCountryOverride } = useUserCountryConfig();

  // Nothing to say unless the viewer was moved out of their own market by an
  // explicit choice. A market reached from the account or the platform
  // default is simply where they belong.
  if (source !== 'override' || !homeCountry) return null;

  return (
    <div
      role="status"
      className="flex items-center gap-2 border-b border-brand-amber/40 bg-brand-amber/15 px-4 py-2 text-sm text-foreground"
    >
      <Globe className="h-4 w-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        You&apos;re browsing <span className="font-semibold">{config.name}</span>.
        Prices and areas shown are for {config.name}.
      </p>
      <button
        type="button"
        onClick={() => setCountryOverride(null)}
        className="shrink-0 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-primary hover:bg-accent"
      >
        Switch to {homeCountry.name}
      </button>
      <button
        type="button"
        onClick={() => setCountryOverride(null)}
        aria-label={`Dismiss and return to ${homeCountry.name}`}
        className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground"
      >
        {/* Dismiss RESTORES rather than hides. Hiding would re-create exactly
            the bug this banner exists for: the wrong market, silently. */}
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

export default MarketNotice;
